import { Directive, DoCheck, inject, input, InputSignal, Signal, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { AbstractControl, ControlEvent, FormGroupDirective, NgForm } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { InputItemModel } from '@libs/form/models';
import { EMPTY, merge, Observable, switchMap } from 'rxjs';
import { ErrorMessageFormatter } from '../../services';

@Directive({
  selector: '[uiFormError]',
  exportAs: 'uiFormError',
  host: {
    '[attr.hidden]': 'isErrorState() ? null: ""',
    '[innerHtml]': 'isErrorState() ? getMessage() : ""',
  },
})
export class ErrorDirective implements DoCheck {
  private readonly errorStateMatcher: ErrorStateMatcher = inject(ErrorStateMatcher);
  private readonly messageFormatter: ErrorMessageFormatter = inject(ErrorMessageFormatter);


  // TODO: IPS-6252 - This doesn’t include cases with FormControlName and custom PARENT_NG_CONTROL token.
  private readonly form: NgForm | FormGroupDirective | null
    = inject(NgForm, { optional: true })
    ?? inject(FormGroupDirective, { optional: true });

  private readonly errorState: WritableSignal<boolean> = signal(false);

  public control: InputSignal<AbstractControl> = input.required();
  public data: InputSignal<InputItemModel> = input.required();

  public readonly isErrorState: Signal<boolean> = this.errorState.asReadonly();

  public constructor() {
    // The error state is made of `touched`, `dirty` and `submitted`, none of which is a signal, so it has to be
    // recomputed on every change the control reports. A submit is reported by the form, it never reaches the control.
    const controlChanges$: Observable<ControlEvent> = toObservable(this.control)
      .pipe(switchMap((control: AbstractControl): Observable<ControlEvent> => control.events));
    const submit$: Observable<unknown> = this.form?.ngSubmit ?? EMPTY;

    merge(controlChanges$, submit$)
      .pipe(takeUntilDestroyed())
      .subscribe(() => void this.updateErrorState());
  }

  /**
   * Validators are attached in the view and revalidate with `emitEvent: false`, a change the control reports
   * to nobody. Recomputing with every check is how Angular Material keeps the state of its own inputs current.
   */
  public ngDoCheck(): void {
    this.updateErrorState();
  }

  protected getMessage(): string {
    return this.messageFormatter.formatMessage(this.control(), this.data());
  }

  private updateErrorState(): void {
    this.errorState.set(this.errorStateMatcher.isErrorState(this.control(), this.form));
  }
}
